import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, Eye, TrendingUp, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { SERVER_URL } from '../../../constants';

const ECommercePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${SERVER_URL}/api/v1/product`);
        const { data } = response.data;
        const trendingProducts = data.filter(product => product.top === 'hotTrending');
        setProducts(trendingProducts);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching products:', error);
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const ProductCard = ({ product }) => {
    const isHovered = hoveredProduct === product._id;
    const discount = Math.round(
      ((product.originalPrice - product.price) / product.originalPrice) * 100
    );

    return (
      <div
        className="min-w-[280px] sm:min-w-[300px] bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 group snap-start"
        onMouseEnter={() => setHoveredProduct(product._id)}
        onMouseLeave={() => setHoveredProduct(null)}
      >
        {/* Image Container */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={product.imgUrl[0] || '/api/placeholder/300/300'}
            alt={product.name}
            className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
          />
          {/* Quick View Button */}
          <button
            onClick={() => navigate(`/product/${product._id}`)}
            className={`absolute top-2 right-2 bg-white p-2 rounded-full shadow-md transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          >
            <Eye size={18} className="text-gray-600 hover:text-blue-600" />
          </button>
          {/* Discount Badge */}
          {discount > 0 && (
            <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">
              -{discount}%
            </div>
          )}
          {/* Low Stock Warning */}
          {product.totalStock < 10 && (
            <div className="absolute bottom-2 right-2 bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded">
              Only {product.totalStock} left
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <h3 className="text-md font-medium text-gray-900 line-clamp-2 min-h-[2.5rem] group-hover:text-blue-600 transition-colors duration-300">
            {product.name}
          </h3>
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, index) => (
              <Star
                key={index}
                size={16}
                fill={index < Math.round(product.averageReview || 4.5) ? "currentColor" : "none"}
                strokeWidth={2}
                className="text-yellow-500"
              />
            ))}
            <span className="text-xs text-gray-600 ml-1">({product.averageReview || 4.5})</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-900">
              ₹{product.price.toLocaleString()}
            </span>
            {product.originalPrice && (
              <span className="text-sm text-gray-500 line-through">
                ₹{product.originalPrice.toLocaleString()}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 line-clamp-2">
            {product.description || "A must-have trending item!"}
          </p>
          <button
            onClick={() => navigate(`/product/${product._id}`)}
            className="w-full bg-blue-600 text-white py-2 rounded-md text-sm font-semibold hover:bg-blue-700 transition-all duration-300"
          >
            View Details
          </button>
        </div>
      </div>
    );
  };

  const ProductListing = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }

    if (!products.length) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <TrendingUp size={48} className="mb-3" />
          <p className="text-lg font-medium">No trending products available</p>
        </div>
      );
    }

    return (
      <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 snap-x snap-mandatory">
        {products.map((product) => (
          <ProductCard key={product._id} product={product} />
        ))}
      </div>
    );
  };

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 animate-fade-in">
          <div className="text-center sm:text-left mb-6 sm:mb-0">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
              Hot <span className="text-blue-600">Trending</span> Products
            </h2>
            <p className="mt-2 text-gray-600 text-md sm:text-lg">
              Check out what’s trending right now!
            </p>
          </div>
          <Link
            to="/categories"
            className="group flex items-center gap-2 text-blue-600 font-semibold text-lg hover:underline transition-all duration-300"
          >
            View All
            <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Product Listing */}
        <ProductListing />
      </div>
    </section>
  );
};

export default ECommercePage;
